import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Server paginator envelope for the pickup / RTO / remittance inner order
 * lists (BaseRepository::result_for_paginate wrapped in `object`).
 */
export interface PaginatedOrders<T> {
  items: T[];
  page: number;
  total: number;
  pages: number;
}

interface UseOrderListPaginationResult<T> {
  items: T[];
  total: number;
  isLoading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
}

/**
 * Generic server-side "load more" for the pickup / RTO / remittance detail
 * order lists (PAGINATE). Fetches one page at a time, accumulates rows, and
 * dedupes by order `uid` (fallback `id`). `hasMore` is derived from server
 * truth (`page < pages`) rather than a page-size length heuristic, so a final
 * page that happens to be exactly full still stops correctly.
 *
 * The `fetcher` MUST be stable (wrap in `useCallback` keyed on the entity uid)
 * — a new identity resets the list back to page 1.
 */
export function useOrderListPagination<T extends { uid?: string; id?: string | number }>(
  fetcher: (page: number) => Promise<PaginatedOrders<T>>,
): UseOrderListPaginationResult<T> {
  const [items, setItems] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const requestIdRef = useRef(0);

  const keyOf = (item: T) => String(item.uid ?? item.id ?? '');

  const merge = useCallback((data: PaginatedOrders<T>, reset: boolean) => {
    setPage(data.page);
    setPages(data.pages);
    setTotal(data.total);
    setItems((prev) => {
      if (reset) {
        return data.items;
      }
      const seen = new Set(prev.map(keyOf));
      return [...prev, ...data.items.filter((item) => !seen.has(keyOf(item)))];
    });
  }, []);

  // Initial load / reset when the fetcher identity changes (new entity).
  useEffect(() => {
    let isMounted = true;
    const currentRequestId = ++requestIdRef.current;

    setItems([]);
    setPage(1);
    setPages(1);
    setTotal(0);
    setIsLoading(true);

    fetcher(1)
      .then((data) => {
        if (!isMounted || currentRequestId !== requestIdRef.current) return;
        merge(data, true);
      })
      .finally(() => {
        if (!isMounted || currentRequestId !== requestIdRef.current) return;
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [fetcher, merge]);

  const hasMore = page < pages;

  const loadMore = useCallback(async () => {
    if (loadingMore || page >= pages) return;

    const nextPage = page + 1;
    const currentRequestId = ++requestIdRef.current;
    setLoadingMore(true);

    try {
      const data = await fetcher(nextPage);
      if (currentRequestId !== requestIdRef.current) return;
      merge(data, false);
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoadingMore(false);
      }
    }
  }, [fetcher, loadingMore, merge, page, pages]);

  return { items, total, isLoading, loadingMore, hasMore, loadMore };
}
